import { observer } from 'mobx-react-lite'
import { Alert, Divider, EmptyCell } from 'shared/ui'
import { type IAlertButtonAction } from 'shared/ui/Alert/types'
import { ModalInfoBlockWithTitle } from 'shared/ui/Modal/ui/ModalInfoBlockWithTitle/ModalInfoBlockWithTitle'
import { type IModalInfoRowProps } from 'shared/ui/Modal/ui/ModalInfoRow/ModalInfoRow'
import { type TollFreeProfileNumber } from 'entities/Compliance/model'
import { ImageCard } from 'entities/Attachment'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { ModalHeader } from 'pages/settings/pages/compliance/ui/ModalHeader/ModalHeader'
import { type ComplianceLayerStore } from 'pages/settings/pages/compliance/store/complianceLayerStore'
import { StatusLine } from '../StatusLine/StatusLine'
import styles from './styles.module.scss'

type IDetailsModalContentProps = {
  tollFreeNumber: TollFreeProfileNumber
  complianceLayerStore: ComplianceLayerStore
  errorMessage: {
    title: string | null
    desc: string
  }
  withUpdata?: boolean
  learnMoreModalAction: () => void
  updateStatusAction: () => void
}

export const DetailsModalContent = observer(
  ({
    tollFreeNumber,
    complianceLayerStore,
    errorMessage,
    withUpdata,
    learnMoreModalAction,
    updateStatusAction,
  }: IDetailsModalContentProps) => {
    const {
      businessInformation,
      pointsOfContact: { firstContact },
    } = complianceLayerStore

    const { individualTollFreeProfile: individualData } = tollFreeNumber || {}

    const {
      website_url,
      business_name,
      street_address_1,
      country,
      isCanadaLocation,
      registration_number,
    } = businessInformation || {}

    const businessInfoLines: IModalInfoRowProps[] = [
      { label: 'Legal business name', value: business_name },
      {
        label: isCanadaLocation ? 'CBN' : 'EIN',
        value: registration_number,
        tooltip: isCanadaLocation ? 'Canadian Business Number' : 'Employer Identification Number',
      },
      {
        label: 'Business website URL',
        value: website_url ? (
          <a href={website_url} target='_blank' rel='noreferrer'>
            {website_url}
          </a>
        ) : (
          '-'
        ),
      },
      { label: 'Business address', value: street_address_1 },
      { label: 'Country', value: country },
    ]

    const contactInfoLines: IModalInfoRowProps[] = [
      {
        label: 'Name',
        value: `${firstContact?.first_name || ''} ${firstContact?.last_name || ''}`,
      },
      {
        label: 'Business email',
        value: firstContact?.email || <EmptyCell />,
      },
      { label: 'Business title', value: firstContact?.business_title || <EmptyCell /> },
      { label: 'Phone number', value: firstContact?.phone_number || <EmptyCell /> },
    ]

    const useCasesLines: IModalInfoRowProps[] = [
      {
        label: 'Name',
        value: individualData?.title || <EmptyCell />,
      },
      {
        label: 'Description',
        value: individualData?.use_case_description || <EmptyCell />,
      },
      {
        label: 'Sample message',
        value: individualData?.sample_message_1 || <EmptyCell />,
      },
      {
        label: 'Privacy Policy URL',
        value: individualData?.privacy_policy_url ? (
          <a href={individualData.privacy_policy_url} target='_blank' rel='noreferrer'>
            {individualData.privacy_policy_url}
          </a>
        ) : (
          <EmptyCell />
        ),
      },
      {
        label: 'Terms and Conditions URL',
        value: individualData?.terms_url ? (
          <a href={individualData.terms_url} target='_blank' rel='noreferrer'>
            {individualData.terms_url}
          </a>
        ) : (
          <EmptyCell />
        ),
      },
      {
        label: 'Estimated monthly messages',
        value: individualData?.monthly_volume || <EmptyCell />,
      },
    ]

    const optInMethodsLines: IModalInfoRowProps[] =
      individualData?.opt_in_data?.map((method) => ({
        label:
          individualData.opt_in_methods?.find((item) => item.id === method.opt_in_method_id)
            ?.title || '',
        value: (
          <div className={styles.images}>
            {method?.files.map((item) => (
              <ImageCard
                key={item.id}
                attachment={
                  new Attachment({
                    imageUrl: item.source_url,
                  })
                }
              />
            ))}
          </div>
        ),
      })) || []

    return (
      <div className={styles.wrap}>
        <ModalHeader
          header={tollFreeNumber.formattedNumber || ''}
          statusData={<StatusLine tollFree={tollFreeNumber} />}
        />

        {errorMessage.title && (
          <Alert
            item={{
              type: 'error',
              title: errorMessage.title,
              desc: "Verify that all the business information fields are correct. Ensure that you have a live website URL. It's best to edit and resubmit within 7 days for a faster review.",
            }}
            actions={
              [
                withUpdata
                  ? {
                      type: 'primary',
                      variant: 'button',
                      text: 'Update',
                      onClick: updateStatusAction,
                    }
                  : undefined,
                {
                  type: 'secondary',
                  variant: 'button',
                  text: 'Learn more',
                  onClick: learnMoreModalAction,
                },
              ].filter((item) => !!item) as IAlertButtonAction[]
            }
          />
        )}

        {!errorMessage.title && <Divider />}

        <ModalInfoBlockWithTitle title='Business info' lines={businessInfoLines} />
        <ModalInfoBlockWithTitle title='Contact info' lines={contactInfoLines} />
        <ModalInfoBlockWithTitle title='Use case' lines={useCasesLines} />
        <ModalInfoBlockWithTitle title='Opt-in method' lines={optInMethodsLines} />
      </div>
    )
  }
)
